import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notification as antdNotification } from 'antd';
import {
  initiateTranscription,
  requestMeetingUploadUrl,
  confirmMeetingUpload,
  saveLiveTranscript,
} from '../services/meetings.service';
import { NotificationContext } from './NotificationProvider';
import { formatFileName } from '../util/formatFileName';

export interface ActiveUpload {
  meetingId: string;
  fileName: string;
  percent: number;
  loaded: number;
  total: number;
  step: 'preparing' | 'uploading' | 'finalizing' | 'done' | 'error';
  transcriptionInitiated?: boolean;
  uploadSource: 'recording' | 'file' | null;
  completedRecordingId?: string;
}

/** A transcript captured live over the streaming WebSocket during an in-person meeting. */
export interface LiveTranscriptPayload {
  cues: Array<{ speaker: string; text: string; startMs: number; endMs: number }>;
  nameMapping: Record<string, string>;
}

export interface UploadStartParams {
  file: File;
  meeting: any;
  login: any;
  attendees: any;
  uploadSource: 'recording' | 'file' | null;
  /**
   * Supplied for live-transcribed meetings. Resolving to a transcript saves it
   * directly and skips the async AssemblyAI job; resolving to `null` — which is
   * what happens whenever streaming failed — falls back to that job.
   */
  liveTranscript?: Promise<LiveTranscriptPayload | null>;
}

interface UploadContextType {
  activeUpload: ActiveUpload | null;
  startUpload: (params: UploadStartParams) => void;
  cancelUpload: () => void;
  clearUpload: (meetingId: string) => void;
}

export const UploadContext = createContext<UploadContextType>({
  activeUpload: null,
  startUpload: () => {},
  cancelUpload: () => {},
  clearUpload: () => {},
});

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const UploadProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeUpload, setActiveUpload] = useState<ActiveUpload | null>(null);
  const uploadXhrRef = useRef<XMLHttpRequest | null>(null);
  const { addNotification, startPollingForTranscript } = useContext(NotificationContext);

  // Warn before page reload while upload is active
  useEffect(() => {
    if (!activeUpload || activeUpload.step === 'done' || activeUpload.step === 'error') return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [activeUpload?.step]);

  const cancelUpload = useCallback(() => {
    if (uploadXhrRef.current) {
      uploadXhrRef.current.abort();
      uploadXhrRef.current = null;
    }
    setActiveUpload(null);
  }, []);

  const clearUpload = useCallback((meetingId: string) => {
    setActiveUpload((prev) => (prev?.meetingId === meetingId ? null : prev));
  }, []);

  const startUpload = useCallback(
    async ({ file, meeting, login, attendees, uploadSource, liveTranscript }: UploadStartParams) => {
      const meetingId = meeting._id;
      const uploadNotifId = `upload-${meetingId}`;

      setActiveUpload({
        meetingId,
        fileName: file.name,
        percent: 0,
        loaded: 0,
        total: file.size,
        step: 'preparing',
        uploadSource,
      });

      addNotification({
        id: uploadNotifId,
        meetingId,
        status: 'loading',
        title: 'Uploading recording',
        message: `${formatFileName(file.name)} is being uploaded…`,
        createdAt: new Date().toISOString(),
        isRead: false,
        isSeen: false,
      });

      // Persistent toast — stays until upload finishes or fails
      antdNotification.info({
        key: uploadNotifId,
        message: 'Uploading Recording',
        description: `${formatFileName(file.name)} is being uploaded…`,
        placement: 'topRight',
        duration: 0,
      });

      try {
        // Step 1: get write-only Azure SAS URL
        const urlRes: any = await requestMeetingUploadUrl({
          meetingId,
          orgId: login.data.orgId,
          userId: login.data.id,
          clientId: meeting.clientid,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          isManualUpload: uploadSource !== 'recording',
        });

        if (urlRes?.hasError || !urlRes?.uploadUrl) {
          setActiveUpload((prev) =>
            prev?.meetingId === meetingId ? { ...prev, step: 'error' } : prev,
          );
          addNotification({
            id: uploadNotifId,
            meetingId,
            status: 'error',
            title: 'Upload failed',
            message: urlRes?.message || 'Failed to get upload URL.',
            createdAt: new Date().toISOString(),
            isRead: false,
            isSeen: false,
          });
          antdNotification.error({
            key: uploadNotifId,
            message: 'Upload Failed',
            description: urlRes?.message || 'Failed to get upload URL.',
            placement: 'topRight',
            duration: 5,
          });
          return;
        }

        // Step 2: PUT directly to Azure via XHR — no Node in the data path
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          uploadXhrRef.current = xhr;
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setActiveUpload((prev) =>
                prev?.meetingId === meetingId
                  ? {
                      ...prev,
                      percent: Math.round((e.loaded / e.total) * 100),
                      loaded: e.loaded,
                      total: e.total,
                      step: 'uploading',
                    }
                  : prev,
              );
            }
          };
          xhr.onload = () => {
            uploadXhrRef.current = null;
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Storage upload failed (${xhr.status})`));
          };
          xhr.onerror = () => {
            uploadXhrRef.current = null;
            reject(new Error('Storage upload failed'));
          };
          xhr.onabort = () => {
            uploadXhrRef.current = null;
            reject(new Error('Upload cancelled'));
          };
          xhr.open('PUT', urlRes.uploadUrl);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader('Content-Type', file.type);
          xhr.send(file);
        });

        // Step 3: confirm — saves metadata, DriveItem mirror, SharePoint stream copy
        setActiveUpload((prev) =>
          prev?.meetingId === meetingId
            ? { ...prev, percent: 100, loaded: file.size, total: file.size, step: 'finalizing' }
            : prev,
        );

        const response: any = await confirmMeetingUpload({
          meetingId,
          orgId: login.data.orgId,
          userId: login.data.id,
          clientId: meeting.clientid,
          _id: urlRes._id,
          transcriptionId: urlRes.transcriptionId,
          finalFileName: urlRes.finalFileName,
          blobPath: urlRes.blobPath,
          directoryPath: urlRes.directoryPath,
          fileType: file.type,
          fileSize: file.size,
        });

        if (response.hasError) {
          setActiveUpload((prev) =>
            prev?.meetingId === meetingId ? { ...prev, step: 'error' } : prev,
          );
          addNotification({
            id: uploadNotifId,
            meetingId,
            status: 'error',
            title: 'Upload failed',
            message: response.message || 'Failed to save recording.',
            createdAt: new Date().toISOString(),
            isRead: false,
            isSeen: false,
          });
          antdNotification.error({
            key: uploadNotifId,
            message: 'Upload Failed',
            description: response.message || 'Failed to save recording.',
            placement: 'topRight',
            duration: 5,
          });
          return;
        }

        const newRecording = response?.data;
        const newRecordingId = newRecording?._id?.toString() || null;

        // A live-transcribed meeting already has its transcript — wait for the
        // mapped result rather than paying AssemblyAI a second time for the same
        // audio. Resolves to null (never rejects meaningfully) when streaming
        // failed, which drops through to the async path below.
        let liveResult: LiveTranscriptPayload | null = null;
        if (liveTranscript) {
          try {
            liveResult = await liveTranscript;
          } catch {
            liveResult = null;
          }
        }

        let transcriptionInitiated = false;

        if (newRecordingId && liveResult && liveResult.cues.length > 0) {
          try {
            await saveLiveTranscript({
              transcriptionId: urlRes.transcriptionId,
              meetingId,
              // Stored on the transcript so a later "Redo" still knows who was in the
              // room; without it the re-run comes back labelled "Speaker A"/"B".
              attendees,
              cues: liveResult.cues,
              nameMapping: liveResult.nameMapping,
            });
            antdNotification.success({
              message: 'Transcript Ready',
              description: 'The live transcript for this meeting has been saved.',
              placement: 'topRight',
              duration: 4,
            });
          } catch {
            // Saving the live transcript failed — the audio is safely stored, so
            // recover by transcribing it the normal way instead of losing it.
            liveResult = null;
            antdNotification.warning({
              message: 'Live Transcript Not Saved',
              description: 'Falling back to standard transcription for this recording.',
              placement: 'topRight',
              duration: 6,
            });
          }
        }

        // Kick off transcription for audio/video files
        if (
          newRecordingId &&
          !liveResult &&
          (file.type.startsWith('audio/') || file.type.startsWith('video/'))
        ) {
          addNotification({
            id: newRecordingId,
            meetingId,
            status: 'loading',
            title: 'Your transcript is currently being processed.',
            message: 'This may take a few moments…',
            createdAt: new Date().toISOString(),
            isRead: false,
            isSeen: false,
          });
          antdNotification.info({
            message: 'Transcription Started',
            description: 'Processing your recording. We will notify you upon completion.',
            placement: 'topRight',
            duration: 3,
          });
          try {
            const transcriptionResponse: any = await initiateTranscription({
              ...newRecording,
              attendees,
              meetingId,
            });
            if (transcriptionResponse?.data?._id) {
              transcriptionInitiated = true;
              startPollingForTranscript({
                recordingId: newRecordingId,
                meetingId,
                transcriptionId: transcriptionResponse.data._id,
              });
            }
          } catch {
            antdNotification.error({
              message: 'Transcription Failed',
              description: 'Could not start transcription automatically. Use the Transcribe button to retry.',
              placement: 'topRight',
              duration: 6,
            });
          }
        }

        // Mark done — carries transcriptionInitiated so MeetingDetails knows whether to show the loading spinner
        setActiveUpload((prev) =>
          prev?.meetingId === meetingId
            ? { ...prev, step: 'done', completedRecordingId: newRecordingId ?? undefined, transcriptionInitiated }
            : prev,
        );

        const meetingTitle = meeting?.title || 'this meeting';
        const clientSuffix = meeting?.clientName ? ` with ${meeting.clientName}` : '';

        addNotification({
          id: uploadNotifId,
          meetingId,
          status: 'success',
          title: 'Recording uploaded',
          message: `${formatFileName(file.name)} — ${formatBytes(file.size)} uploaded successfully to ${meetingTitle}${clientSuffix}.`,
          createdAt: new Date().toISOString(),
          isRead: false,
          isSeen: false,
        });

        // Swap loading toast → success toast
        antdNotification.success({
          key: uploadNotifId,
          message: 'Recording Uploaded',
          description: `${formatFileName(file.name)} has been uploaded successfully to ${meetingTitle}${clientSuffix}.`,
          placement: 'topRight',
          duration: 5,
          style: { cursor: 'pointer' },
          onClick: () => { window.location.href = `/meetings/${meetingId}`; },
        });
      } catch (err: any) {
        if (err.message === 'Upload cancelled') {
          antdNotification.destroy(uploadNotifId);
          setActiveUpload(null);
          return;
        }
        setActiveUpload((prev) =>
          prev?.meetingId === meetingId ? { ...prev, step: 'error' } : prev,
        );
        addNotification({
          id: uploadNotifId,
          meetingId,
          status: 'error',
          title: 'Upload failed',
          message: err.message || 'File upload failed.',
          createdAt: new Date().toISOString(),
          isRead: false,
          isSeen: false,
        });
        antdNotification.error({
          key: uploadNotifId,
          message: 'Upload Failed',
          description: err.message || 'File upload failed.',
          placement: 'topRight',
          duration: 5,
        });
      }
    },
    [addNotification, startPollingForTranscript],
  );

  return (
    <UploadContext.Provider value={{ activeUpload, startUpload, cancelUpload, clearUpload }}>
      {children}
    </UploadContext.Provider>
  );
};
